import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import {
  ThemeRouteLanguageCode,
  ThemeRouteTranslations,
} from '@/types/theme-routes.types';
import { useTranslateHarborContentMutation } from '@/services/HarborManagementService';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';

export interface ThemeRoutePreviewModalProps extends Omit<
  ModalProps,
  'children'
> {
  name: string;
  defaultLanguage: ThemeRouteLanguageCode;
  description?: string;
  translations: ThemeRouteTranslations;
  source_language?: string;
  onTranslationsGenerated?: (translations: ThemeRouteTranslations) => void;
}

interface ThemeRouteLanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  name: string;
  description: string;
  isDefault: boolean;
  isLoading: boolean;
  onChange: (value: string) => void;
}

const ThemeRouteLanguagePreview: React.FC<ThemeRouteLanguagePreviewProps> = ({
  language,
  name,
  description,
  isDefault,
  isLoading,
  onChange,
}) => {
  const intl = useIntl();

  return (
    <div className="bg-white contrast:bg-black shadow-sm hover:shadow-md contrast:hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-[#dbdbdb] contrast:border-gray-700 contrast:hover:border-gray-600">
      <div className="bg-gray-50 contrast:bg-gray-100 px-5 py-3 flex items-center justify-between border-b border-gray-100 contrast:border-gray-700">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-800 contrast:text-white flex items-center">
            {language.label}
            {isDefault && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 contrast:bg-blue-900 text-blue-800 contrast:text-blue-100">
                {intl.formatMessage({
                  id: 'COMMON.DEFAULT_LANGUAGE',
                  defaultMessage: 'Default',
                })}
              </span>
            )}
            {isLoading && (
              <div className="ml-2 w-4 h-4 border-2 border-blue-500 contrast:border-blue-400 border-t-transparent rounded-full animate-spin" />
            )}
          </h2>
        </div>
      </div>

      <div className="p-5">
        <div className="mb-4">
          <span
            id={`name-label-${language.code}`}
            className="block text-sm font-medium text-gray-700 contrast:text-white mb-2"
          >
            {intl.formatMessage({ id: 'COMMON.NAME', defaultMessage: 'Name' })}
          </span>
          <p
            id={`name-${language.code}`}
            className="text-base font-medium text-gray-800 contrast:text-white bg-gray-50 contrast:bg-gray-100 px-3 py-2 rounded-lg"
            aria-labelledby={`name-label-${language.code}`}
            role="text"
          >
            {name ||
              intl.formatMessage({
                id: 'COMMON.NOT_PROVIDED',
                defaultMessage: 'Not provided',
              })}
          </p>
        </div>

        <div className="mb-0">
          <label
            htmlFor={`description-${language.code}`}
            className="block text-sm font-medium text-gray-700 contrast:text-white mb-2"
          >
            {intl.formatMessage({
              id: 'COMMON.DESCRIPTION',
              defaultMessage: 'Description',
            })}
          </label>
          <textarea
            id={`description-${language.code}`}
            className="textarea textarea-bordered w-full rounded-lg border-gray-300 contrast:border-gray-600 contrast:bg-black contrast:text-white contrast:placeholder:text-gray-400 min-h-[100px] text-sm focus:border-blue-500 contrast:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 contrast:focus:ring-blue-400/30"
            value={description}
            onChange={(e) => onChange(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'THEME_ROUTES.PREVIEW.DESCRIPTION_PLACEHOLDER',
              defaultMessage: 'Edit translation for this language',
            })}
          />
        </div>
      </div>
    </div>
  );
};

const ThemeRoutePreviewModal: React.FC<ThemeRoutePreviewModalProps> = ({
  name,
  defaultLanguage,
  description,
  translations,
  source_language,
  open,
  onClose,
  className,
  onTranslationsGenerated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [localTranslations, setLocalTranslations] =
    useState<ThemeRouteTranslations>(() => {
      const base: ThemeRouteTranslations = { ...translations };
      const baseDesc =
        description || translations[defaultLanguage]?.description || '';
      base[defaultLanguage] = {
        ...base[defaultLanguage],
        description: baseDesc,
      };
      return base;
    });

  const [isLoading, setIsLoading] = useState(false);
  const [translateHarborContent] = useTranslateHarborContentMutation();

  useEffect(() => {
    if (!open) return;
    const base: ThemeRouteTranslations = { ...translations };
    const baseDesc =
      description || translations[defaultLanguage]?.description || '';
    base[defaultLanguage] = {
      ...base[defaultLanguage],
      description: baseDesc,
    };
    setLocalTranslations(base);
  }, [open, translations, description, defaultLanguage]);

  const syncToParent = useCallback(
    (next: ThemeRouteTranslations) => {
      if (onTranslationsGenerated) {
        onTranslationsGenerated(next);
      }
    },
    [onTranslationsGenerated]
  );

  const handleDescriptionChange = (
    langCode: ThemeRouteLanguageCode,
    value: string
  ) => {
    setLocalTranslations((prev) => {
      const next: ThemeRouteTranslations = {
        ...prev,
        [langCode]: {
          ...prev[langCode],
          description: value,
        },
      };
      syncToParent(next);
      return next;
    });
  };

  const hasFetchedRef = useRef(false);

  const fetchTranslations = useCallback(async () => {
    const baseDesc =
      description || translations[defaultLanguage]?.description || '';

    if (!baseDesc || !baseDesc.trim()) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'THEME_ROUTES.VALIDATION.DESCRIPTION_REQUIRED_FOR_PREVIEW',
          defaultMessage: 'Please enter description to translate',
        }),
        {
          variant: 'warning',
        }
      );
      return;
    }

    setIsLoading(true);
    try {
      const result = await translateHarborContent({
        description: baseDesc,
        source_language: source_language || defaultLanguage || 'en',
      }).unwrap();

      if (result && result.warning_message) {
        enqueueSnackbar(result.warning_message, {
          variant: 'warning',
        });
      }

      setLocalTranslations((prev) => {
        let next: ThemeRouteTranslations = { ...prev };

        if (result && result.translations) {
          Object.entries(result.translations).forEach(([langCode, value]) => {
            const code = langCode as ThemeRouteLanguageCode;
            next = {
              ...next,
              [code]: {
                ...next[code],
                description: value?.description,
              },
            };
          });
        }

        syncToParent(next);
        return next;
      });

      if (
        !result.warning_message &&
        Object.keys(result?.translations || {}).length > 0
      ) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'THEME_ROUTES.PREVIEW.TRANSLATION_SUCCESS',
            defaultMessage: 'Translations generated successfully',
          }),
          {
            variant: 'success',
          }
        );
      }
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'THEME_ROUTES.PREVIEW.TRANSLATION_ERROR',
          defaultMessage: 'Failed to generate translations',
        }),
        {
          variant: 'error',
        }
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    enqueueSnackbar,
    translateHarborContent,
    description,
    translations,
    source_language,
    defaultLanguage,
    syncToParent,
    intl,
  ]);

  useEffect(() => {
    if (!open) {
      hasFetchedRef.current = false;
      return;
    }

    if (hasFetchedRef.current) return;

    const hasNonDefaultDescription = Object.entries(translations).some(
      ([code, value]) => {
        const langCode = code as ThemeRouteLanguageCode;
        if (langCode === defaultLanguage) return false;
        const desc = value?.description;
        return typeof desc === 'string' && desc.trim() !== '';
      }
    );

    if (!hasNonDefaultDescription) {
      hasFetchedRef.current = true;
      void fetchTranslations();
    }
  }, [open, translations, defaultLanguage, fetchTranslations]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="theme-route-preview-modal-title"
    >
      <div className="harbor-preview-modal bg-white contrast:bg-black w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-hidden">
        <div className="modal-header px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 contrast:from-blue-900 contrast:to-blue-950 border-b border-blue-200 contrast:border-blue-800">
          <div className="modal-header-row flex items-center justify-between">
            <div>
              <h1
                id="theme-route-preview-modal-title"
                className="text-xl font-semibold text-white contrast:text-white"
              >
                {intl.formatMessage({
                  id: 'THEME_ROUTES.PREVIEW.TITLE',
                  defaultMessage: 'Theme route translations preview',
                })}
              </h1>
              <p className="text-blue-100 contrast:text-blue-200 text-sm mt-1">
                {intl.formatMessage({
                  id: 'THEME_ROUTES.PREVIEW.DESCRIPTION',
                  defaultMessage:
                    'Review and edit translations for the route description in all supported languages.',
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-gray-50 contrast:bg-gray-100 rounded-lg p-4 mb-6">
            <div className="info-header-row flex items-start justify-between">
              <div className="flex-1">
                <h2 className="font-semibold text-gray-800 contrast:text-white mb-1">
                  {name ||
                    intl.formatMessage({
                      id: 'THEME_ROUTES.PREVIEW.UNNAMED_ROUTE',
                      defaultMessage: 'Unnamed route',
                    })}
                </h2>
              </div>
            </div>
          </div>

          <div className="lang-list overflow-y-auto max-h-[50vh] pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => {
              const code = lang.code as ThemeRouteLanguageCode;
              const desc = localTranslations[code]?.description || '';
              return (
                <div key={lang.code}>
                  <ThemeRouteLanguagePreview
                    language={lang}
                    name={name}
                    description={desc}
                    isDefault={code === defaultLanguage}
                    isLoading={isLoading}
                    onChange={(value) => handleDescriptionChange(code, value)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 contrast:bg-gray-100 border-t border-gray-100 contrast:border-gray-700">
          <div className="footer-actions-row flex justify-between items-center">
            <button
              type="button"
              onClick={fetchTranslations}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-green-600 contrast:bg-green-700 text-white contrast:text-white text-sm font-medium rounded-lg hover:bg-green-700 contrast:hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 contrast:focus:ring-green-400 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={intl.formatMessage({
                id: 'THEME_ROUTES.PREVIEW.REGENERATE_ARIA',
                defaultMessage: 'Regenerate translations',
              })}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {intl.formatMessage({
                    id: 'COMMON.GENERATING',
                    defaultMessage: 'Generating',
                  })}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {intl.formatMessage({
                    id: 'COMMON.REGENERATE',
                    defaultMessage: 'Regenerate',
                  })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 contrast:bg-blue-700 text-white contrast:text-white text-sm font-medium rounded-lg hover:bg-blue-700 contrast:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 contrast:focus:ring-blue-400 shadow-sm transition-colors duration-200"
              aria-label={intl.formatMessage({
                id: 'THEME_ROUTES.PREVIEW.CLOSE_MODAL_ARIA',
                defaultMessage: 'Close preview modal',
              })}
            >
              {intl.formatMessage({
                id: 'COMMON.CLOSE',
                defaultMessage: 'Close',
              })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ThemeRoutePreviewModal;
